import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PageWrapper } from '../components/partials';
import WarningMessage from '../components/utilities/WarningMessage';
import Spinner from '../components/widgets/Spinner';
import withTranslate from '../containers/widgets/withTranslate';
import { getUserData } from '../selectors/user';
import { CUSTOMER_TIERS } from '../utils/consts';
import { ACCOUNT_PLAN_EVENT_PREFIX, trackMixpanelEvent } from '../utils/general';
import Features from './components/Features';
import OrgPlanDetails from './components/OrgPlanDetails';
import PacingCard from './components/PacingCard';
import UpgradePlans from './components/UpgradePlans';
import { PACING_IDS, MIXPANEL_PAGE_LOADED, PACING_TYPES } from './consts';
import { useFetchAccountPlan } from './hooks';
import css from './OrganizationPlan.css';
import { buildWebConversionsReportUrl, getOrganizationPlanStartDate } from './utils';

function OrganizationPlan({ translate }) {
    const upgradePlansRef = useRef(null);
    const { isLoading, error, ...data } = useFetchAccountPlan();
    const [userClosedWarningMsg, setUserClosedWarningMsg] = useState(false);
    const {
        organization_tier: tier = '',
        company_display_name: orgName = '',
        should_block_app: orgIsBlocked = false,
        is_paying_customer: isPaying = false,
    } = useSelector(state => getUserData(state));
    const {
        timezone = '',
        currency = '',
        contractStartDate = '',
        contractEndDate = '',
        pacingStartDate = '',
        daysLeft = 0,
        actualInstalls = 0,
        prevActualInstalls = 0,
        actualWebConversions = 0,
        actualPcConsoleInstalls = 0,
        totalActualConversions = 0,
        actualAdSpend = 0,
        planPrice = 0,
        pacingDurationInYears = 0,
        found: hasAccountPlan = false,
        isSfdcUnique = false,
        features,
        alerts = [],
        caps = {},
        selfServeConversionsRollover = 0,
        actualEvents = 0
    } = data || {};
    const totalConversionsCap = data.totalConversionsCap === -1 ? Infinity : data.totalConversionsCap || 0;
    const adSpendCap = data.adSpendCap === -1 ? Infinity : data.adSpendCap || 0;
    // Exclude rollover when deciding card type — rollover alone on an ad-spend plan must not flip to conversions view
    const sfConversionsCap = totalConversionsCap === Infinity ? Infinity : totalConversionsCap - selfServeConversionsRollover;
    const isMultiYearContract = pacingDurationInYears > 1;
    const planStartDate = getOrganizationPlanStartDate(tier, pacingStartDate, contractStartDate);
    const webConversionsReportLink = buildWebConversionsReportUrl(planStartDate);

    useEffect(() => {
        if (orgName) {
            trackMixpanelEvent(ACCOUNT_PLAN_EVENT_PREFIX, MIXPANEL_PAGE_LOADED, { blocked: orgIsBlocked });
        }
    }, [orgName, orgIsBlocked]);

    const showWarningMessage = orgIsBlocked && !userClosedWarningMsg;
    const isFreeTier = tier === CUSTOMER_TIERS.FREE;
    const type = sfConversionsCap ? PACING_TYPES.CONVERSIONS : PACING_TYPES.AD_SPEND;
    const conversionProgressSections = [
        {
            id: PACING_IDS.WEB,
            titleKey: 'STATIC.PAGES.ACCOUNT_PLAN.CONVERSIONS.WEB',
            value: actualWebConversions,
            progressBarTooltipKey: 'STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PROGRESS_BAR_WEB',
            breakdownTooltipKey: webConversionsReportLink ? 'STATIC.PAGES.ACCOUNT_PLAN.CONVERSIONS.VIEW_REPORT' : undefined,
            reportLink: webConversionsReportLink,
        },
        {
            id: PACING_IDS.MOBILE,
            titleKey: 'STATIC.PAGES.ACCOUNT_PLAN.CONVERSIONS.MOBILE',
            value: actualInstalls + prevActualInstalls + actualPcConsoleInstalls,
            progressBarTooltipKey: 'STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PROGRESS_BAR_MOBILE',
        },
    ];
    const progressSections =
        type === PACING_TYPES.CONVERSIONS
            ? conversionProgressSections
            : [{ id: PACING_IDS.AD_SPEND, value: actualAdSpend }];

    const pacingTextWrapper = translationKey =>
        `${isMultiYearContract ? `${translate('STATIC.PAGES.ACCOUNT_PLAN.YEARLY')} ` : ''}${translate(translationKey)}`;

    const getOrganizationPlanPage = () => {
        if (!Object.keys(data).length) return <></>;
        return (
            <>
                <WarningMessage
                    show={showWarningMessage}
                    className={css.warningMessage}
                    showIcon
                    showTypeIcon
                    onClose={() => setUserClosedWarningMsg(true)}
                    type="error"
                    message="STATIC.PAGES.ACCOUNT_PLAN.BLOCKED_ORF_ERROR_MSG"
                />
                <div className={css.pageHeader}>
                    {translate('STATIC.PAGES.ACCOUNT_PLAN.HEADER')} - {orgName}
                </div>
                <div className={css.pageContent}>
                    {hasAccountPlan && isSfdcUnique && (
                        <>
                            <OrgPlanDetails
                                type={type}
                                timezone={timezone}
                                currency={currency}
                                contractStartDate={getOrganizationPlanStartDate(
                                    tier,
                                    pacingStartDate,
                                    contractStartDate
                                )}
                                contractEndDate={contractEndDate}
                                planPrice={planPrice}
                                totalConversionsCap={totalConversionsCap}
                                adSpendCap={adSpendCap}
                                pacingTextWrapper={pacingTextWrapper}
                                upgradePlansRef={upgradePlansRef}
                                alerts={alerts}
                                caps={caps}
                                selfServeConversionsRollover={selfServeConversionsRollover}
                                actualEvents={actualEvents}
                            />
                            <PacingCard
                                type={type}
                                progressSections={progressSections}
                                totalValue={Math.round(
                                    type === PACING_TYPES.CONVERSIONS ? totalActualConversions : actualAdSpend
                                )}
                                maxValue={Math.round(
                                    type === PACING_TYPES.CONVERSIONS ? totalConversionsCap : adSpendCap
                                )}
                                expiresInDays={daysLeft}
                                isFreeTier={isFreeTier}
                                isPaying={isPaying}
                                pacingTextWrapper={pacingTextWrapper}
                            />
                            {type === PACING_TYPES.AD_SPEND && totalActualConversions > 0 && (
                                <PacingCard
                                    type={PACING_TYPES.CONVERSIONS}
                                    progressSections={conversionProgressSections}
                                    totalValue={Math.round(totalActualConversions)}
                                    maxValue={Math.round(totalActualConversions) || 1}
                                    hideCap
                                    pacingTextWrapper={pacingTextWrapper}
                                />
                            )}
                            {!isFreeTier && features && <Features features={features} />}
                        </>
                    )}
                    {isFreeTier && <UpgradePlans hasAccountPlan={hasAccountPlan} ref={upgradePlansRef} />}
                </div>
            </>
        );
    };

    return (
        <PageWrapper className={css.organizationPlanPage} disablePadding={!!showWarningMessage}>
            {isLoading && !error ? <Spinner style={{ top: '50vh' }} show expanded /> : getOrganizationPlanPage()}
        </PageWrapper>
    );
}

OrganizationPlan.propTypes = {
    translate: PropTypes.func,
};

export default withTranslate(OrganizationPlan);
